import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Stack,
  Typography,
} from '@mui/material';
import {
  GridRenderCellParams,
  GridColDef,
  GridToolbarContainer,
  useGridApiContext,
  DataGrid,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid';
import {useSelector} from 'react-redux';
import {useQueryClient} from '@tanstack/react-query';
import {AccountTree, LogoutOutlined} from '@mui/icons-material';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {IStation} from '../../../../../dto/Station.dto';
import {IPlayerDeviceDetail} from '../../../../../dto/PlayerDeviceDetail.dto';
import {useRqListStationDevices} from '../../../../../react_query/player_device_details/ListStationDevices';
import {useStylesed} from '../../../../../components/styles/commonStyled';
import IconLink from '../../../../../asset/icons/icon_link.svg';
import QRCodeLoginButton from './QRCodeLogin';
import {PlayerUpdate} from './StationListCmpt';
import {UserCanAny} from '../../../../../components/UserCan';
import {DialogClose} from '../../../../../components/dialogs/DialogFrame/DialogClose';
import DeleteDeviceBtn from './DeleteDeviceBtn';
import {StationLoginInfoBtn} from './StationLoginInfoBtn';
import IconSearch from '../../../../../asset/icons/icon_search.svg';
import MusicMixUpdateSwitch from './MusicMixUpdateSwitch';
import StationStingerPlayCount from './StationStingerPlayCount';
import StationRefreshRadioPlaylists from './StationRefershRadioPlaylists';
import MirrorThisStation from './MirrorThisStation';
import {companyIdSelector} from '../../../../../store/Slices/companySlice';
import {useUserCanAny} from '../../../../../hooks/useUserCan';
import {IStationWithParent} from '../../../../../react_query/stations/CompanyStationList';
import {commonDateTimeFormat} from '../../../../../Service/CommonService';
import NameUpdateAliasShow from '../../../../../components/MUI/TextField/NameUpdateAliasShow';
import {StationIsPaidIcon} from './StationMgmt';

export const DeviceInfoDialog = ({
  station,
  setSelectedStation,
}: {
  station: IStationWithParent;
  setSelectedStation: React.Dispatch<
    React.SetStateAction<IStationWithParent | null>
  >;
}) => {
  const classes = useStylesed();
  const queryClient = useQueryClient();
  const companyId = useSelector(companyIdSelector);
  const isStationNameEdit = useUserCanAny('is-admin');
  const [open, setOpen] = useState<boolean>(true);

  const [playerDeviceInfo, setPlayerDeviceInfo] = useState<
    IPlayerDeviceDetail[] | null
  >(null);

  const {data, isError, isFetching} = useRqListStationDevices(
    station.company_id,
    station.id,
  );

  const handleClose = useCallback(() => {
    setOpen(false);
    setSelectedStation(null);
  }, [setOpen, setSelectedStation]);

  useEffect(() => {
    if (!isError && !isFetching && data) {
      const [playerDeviceInfoData, stationDetails] = data;
      setPlayerDeviceInfo(playerDeviceInfoData as IPlayerDeviceDetail[]);
      setSelectedStation(prev => {
        if (prev) {
          return {
            ...prev,
            password: stationDetails.password,
          };
        }
        return prev;
      });
    }
  }, [data, isError, isFetching, setPlayerDeviceInfo, setSelectedStation]);

  const getRowClassName = useCallback(() => {
    return classes.cls_active;
  }, [classes.cls_active]);

  const columns: GridColDef[] = useMemo(() => {
    return [
      {
        field: 'unique_id',
        headerName: 'Device Unique ID',
        sortable: true,
        flex: 1,
      },
      {
        field: 'login_pin',
        headerName: 'Code',
        sortable: false,
        width: 100,
      },
      {
        field: 'platform',
        headerName: 'Platform',
        sortable: true,
        width: 100,
      },
      {
        field: 'version',
        headerName: 'Version',
        sortable: true,
        width: 150,
      },
      {
        field: 'timezone',
        headerName: 'Timezone',
        sortable: true,
        width: 120,
      },
      {
        field: 'installed_at',
        headerName: 'Installed On',
        sortable: true,
        flex: 1,
        renderCell: (params: GridRenderCellParams<IPlayerDeviceDetail>) => {
          return (
            <Typography>
              {commonDateTimeFormat(params.row.installed_at!)}
            </Typography>
          );
        },
      },
      {
        field: 'last_active_on',
        headerName: 'Last Connected ',
        sortable: true,
        flex: 1,
        renderCell: (params: GridRenderCellParams<IPlayerDeviceDetail>) => {
          return (
            <Typography>
              {commonDateTimeFormat(params.row.last_active_on!)}
            </Typography>
          );
        },
      },
      {
        field: 'sync_completed_at',
        headerName: 'Sync Completed On',
        sortable: true,
        flex: 1,
        renderCell: (params: GridRenderCellParams<IPlayerDeviceDetail>) => {
          return (
            <Typography>
              {commonDateTimeFormat(
                params.row.sync_completed_at
                  ? params.row.sync_completed_at
                  : params.row.last_active_on ?? null,
              )}
            </Typography>
          );
        },
      },
      {
        field: 'action',
        headerName: 'Action',
        sortable: false,
        width: '300',
        renderCell: (params: GridRenderCellParams<IPlayerDeviceDetail>) => {
          if (params.row.deleted_at) {
            return (
              <Stack direction='row' alignItems='center' spacing={1}>
                <Chip
                  icon={<LogoutOutlined />}
                  label='Logged Out'
                  color='error'
                />
              </Stack>
            );
          }
          return (
            <UserCanAny scope='is-admin,player-devices:logout'>
              <DeleteDeviceBtn
                companyId={station.company_id}
                parentStationId={station.id}
                stationId={params.row.station_id}
                rowId={params.row.unique_id}
              />
            </UserCanAny>
          );
        },
      },
    ] as GridColDef[];
  }, [station.company_id, station.id]);

  const onSuccess = useCallback(
    (value: string) => {
      setSelectedStation(prev => {
        if (prev) {
          return {...prev, name: value};
        }
        return prev;
      });
      queryClient.invalidateQueries({
        queryKey: ['api/v1/company', Number(companyId), 'stations'],
        refetchType: 'active',
      });
    },
    [companyId, queryClient, setSelectedStation],
  );

  return (
    <Dialog fullWidth maxWidth={false} open={open} onClose={handleClose}>
      <DialogTitle component={'div'}>
        <Stack
          direction='row'
          alignItems='center'
          className='w-full'
          justifyContent='space-between'
          spacing={2}>
          <Stack direction='row' alignItems='center' gap={1}>
            <img src={IconLink} alt='link-icon' className='pr-3' />
            <Typography>Device Connected</Typography>
          </Stack>

          <Stack direction='row' alignItems='center'>
            <StationIsPaidIcon isPaid={station.is_paid} />
            <NameUpdateAliasShow
              name={station.name}
              onSuccess={onSuccess}
              url={`v1/company/${companyId}/station/${station.id}?_method=PUT`}
              payloadKey='station_name'
              isEditAllowed={isStationNameEdit}
            />
          </Stack>
          {station.parent_station && (
            <Stack direction='row' alignItems='center' gap={1} maxWidth={400}>
              <AccountTree />
              <Typography title={`Parent: ${station.parent_station.name}`}>
                {station.parent_station.name}
              </Typography>
            </Stack>
          )}
          <Stack direction='row' alignItems='center' gap={1}>
            <Typography>Close Device Info</Typography>
            <DialogClose onClose={handleClose} />
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Box sx={{height: 'calc(100vh - 200px)'}}>
          <DataGrid
            slots={{toolbar: CustomizedToolBar}}
            disableColumnSelector
            rows={playerDeviceInfo ?? []}
            columns={columns}
            loading={isFetching}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 100,
                },
              },
            }}
            sx={{
              '@media print': {
                '.MuiDataGrid-main': {color: 'rgba(0, 0, 0, 0.87)'},
              },
            }}
            getRowClassName={getRowClassName}
            pageSizeOptions={[20, 50, 75, 100]}
            disableRowSelectionOnClick
            slotProps={{
              columnsPanel: {
                disableHideAllButton: true,
                disableShowAllButton: true,
              },
              toolbar: {
                station,
                setSelectedStation,
              },
            }}
            getRowHeight={() => 'auto'}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const CustomizedToolBar = ({
  station,
  setSelectedStation,
}: {
  station: IStationWithParent;
  setSelectedStation: React.Dispatch<React.SetStateAction<IStation | null>>;
}) => {
  const queryClient = useQueryClient();
  const companyId = useSelector(companyIdSelector);
  const [isStingerIntervalEdit, setIsStingerIntervalEdit] = useState(false);
  const [stingerInterval, setStingerInterval] = useState(
    station.preset_stinger_interval ?? 0,
  );
  const isStationLabelEdit = useUserCanAny('is-admin,station:update');

  const handleCancel = useCallback(() => {
    setStingerInterval(station.preset_stinger_interval ?? 0);
    setIsStingerIntervalEdit(false);
  }, [station.preset_stinger_interval]);

  const onSuccess = useCallback(
    (value: string) => {
      setSelectedStation(prev => {
        if (prev) {
          return {...prev, label: value};
        }
        return prev;
      });
      queryClient.invalidateQueries({
        queryKey: ['api/v1/company', Number(companyId), 'stations'],
        refetchType: 'active',
      });
    },
    [companyId, queryClient, setSelectedStation],
  );

  return (
    <>
      <Stack
        className='flex w-full items-center justify-between mb-3'
        direction='row'>
        <GridToolbarContainer>
          <GridToolbarQuickFilter
            quickFilterParser={(searchInput: string) =>
              searchInput
                .split(',')
                .map(value => value.trim())
                .filter(value => value !== '')
            }
            variant='outlined'
            InputProps={{
              startAdornment: (
                <InputAdornment position='start' className='mr-2'>
                  <img src={IconSearch} alt='search icon' />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiInputBase-root': {
                borderRadius: '100px !important',
                background: 'var(--blue-variant6)',
                minHeight: '40px',
              },
            }}
          />
        </GridToolbarContainer>
        <Stack spacing={3} direction='row'>
          <StationLoginInfoBtn
            stationLoginInfo={{
              name: station.name,
              password: station.password || '',
            }}
          />
          <UserCanAny scope='is-admin,station:generate-qr'>
            <QRCodeLoginButton
              company_id={Number(station.company_id)}
              station_id={Number(station.id)}
            />
          </UserCanAny>
          {!station.parent_id && (
            <UserCanAny scope='is-admin,station:refresh-playlist'>
              <StationRefreshRadioPlaylists
                company_id={station.company_id}
                station_id={station.id}
                station_name={station.name}
              />
            </UserCanAny>
          )}
          {!station.parent_id && (
            <UserCanAny scope='is-admin,station:mirror'>
              <MirrorThisStation station={station} />
            </UserCanAny>
          )}

          <RenderExportButton />
        </Stack>
      </Stack>
      <Stack
        className='p-4'
        sx={{background: 'var(--blue_variant29)'}}
        direction='row'
        alignItems='center'
        justifyContent='space-between'>
        <Stack direction='row' alignItems='center' spacing={1}>
          <Typography>
            <b>Label:</b>
          </Typography>
          <NameUpdateAliasShow
            name={station.label}
            onSuccess={onSuccess}
            url={`v1/company/${companyId}/station/${station.id}?_method=PUT`}
            payloadKey='station_label'
            isEditAllowed={isStationLabelEdit}
          />
        </Stack>
        {station.type === 'odo_station' ? (
          <StationStingerPlayCount
            stationId={station.id}
            isStingerIntervalEdit={isStingerIntervalEdit}
            setIsStingerIntervalEdit={setIsStingerIntervalEdit}
            stingerInterval={stingerInterval}
            setStingerInterval={setStingerInterval}
            setSelectedStation={setSelectedStation}
            handleCancel={handleCancel}
          />
        ) : null}
        <MusicMixUpdateSwitch
          stationId={station.id}
          setSelectedStation={setSelectedStation}
          custom_music_mix={station.custom_music_mix}
        />
        <PlayerUpdate
          stationId={station.id}
          player_update_disabled={Boolean(station.player_update_disabled_at)}
          setSelectedStation={setSelectedStation}
        />
        <Typography variant='subtitle1'> Latest Version </Typography>
        <Typography variant='subtitle1'>{station.player_version}</Typography>
      </Stack>
    </>
  );
};

export const RenderExportButton = () => {
  const apiRef = useGridApiContext();

  const exportOptions = {
    csv: apiRef.current.exportDataAsCsv,
    print: apiRef.current.exportDataAsPrint,
  };

  return (
    <>
      {Object.entries(exportOptions).map(([exportLable, exportOption]) => (
        <Button
          key={exportLable}
          variant='outlined'
          onClick={() => exportOption()}>
          {exportLable}
        </Button>
      ))}
    </>
  );
};
